'use client'

import Image from 'next/image'
import Container from '@/components/structure/Container'
import Title from '@/components/content/common/Title'
import Slider from '@/components/ui/Slider'
import AppIcon from '@/components/content/app/AppIcon'
import Link from '@/components/ui/Link'
import Badge from '@/components/ui/Badge'

import { Icon } from '@/icons/Icon'
import { FeaturedPlacement, trackFeaturedClick } from '@/lib/analytics'
import type { DealCardApp } from '@/app/(app)/fetchers'
import type { AppCategoryRecommendation, Category, Deal, Media } from '@/payload-types'
import { getBrandColors, hexToRgba } from '@/lib/utils'
import { determineDealsToDisplay } from '@/components/content/deals/helpers'

type HomeDealsProps = {
  apps: DealCardApp[]
}

export default function HomeDeals({ apps }: HomeDealsProps) {
  return (
    <div>
      <Container reduced={true} className="flex flex-col gap-14">
        <Title
          title="Exclusive <span>deals</span> you cannot find anywhere else"
          subtitle="App Deals"
          icon={<Icon name="deal" />}
          paragraphs={[
            "We don't just find the best tools, we negotiate the best deals. Limited time offers. Fresh deals monthly.",
          ]}
          buttons={[
            { label: 'Find top deals', link: '/deals' },
            { label: 'Subscribe to updates', link: '/subscribe' },
          ]}
          type="primary"
        />
      </Container>

      <Slider className="mt-10 h-[340px] xs:h-[420px]">
        {apps.map((app) => (
          <DealCard key={app.id} app={app} />
        ))}
      </Slider>
    </div>
  )
}

export function DealCard({ app }: { app: DealCardApp }) {
  const deals = (app.deals?.docs as Deal[] | undefined) ?? []
  const dealsToDisplay = determineDealsToDisplay(deals)

  const appRecs = app.categoryRecommendations?.docs as AppCategoryRecommendation[]
  const primaryCategory = appRecs?.find((rec) => rec.classification === 'primary')
    ?.category as Category

  const homepageImage = app.homepageImage as Media
  const rawUrl = homepageImage?.url || null
  const homepageImageUrl = rawUrl ? encodeURI(rawUrl) : undefined

  const { primaryColor, secondaryColor, tertiaryColor, backgroundColor } = getBrandColors(app)
  return (
    <Link
      href={`/deals/${app.slug}`}
      onClick={() => trackFeaturedClick(FeaturedPlacement.HOME_DEALS, app.name)}
      // Anchors are natively draggable, which hijacks Slider's pointer-based
      // drag gesture with the browser's own HTML5 drag-and-drop. Disable it.
      draggable={false}
      className="flex w-[600px] flex-col gap-3"
    >
      <div
        className="group relative h-[280px] w-full grow cursor-pointer overflow-hidden rounded-3xl border border-gray-200 bg-black xs:h-[350px]"
        style={{
          background: `radial-gradient(70% 100% at 0% 0%, ${hexToRgba(primaryColor, 0.25)} 0%, ${hexToRgba(primaryColor, 0)} 80%),
        radial-gradient(70% 100% at 100% 0%, ${hexToRgba(secondaryColor, 0.25)} 0%, ${hexToRgba(secondaryColor, 0)} 80%),
        radial-gradient(100% 100% at 50% 0%, ${hexToRgba(tertiaryColor, 0.25)} 0%, ${hexToRgba(tertiaryColor, 0)} 80%, rgba(0, 0, 0, 0) 100%),
        #000`,
        }}
      >
        <Badge count={dealsToDisplay.length} icon="deal" className="absolute left-3 top-3 z-20" />

        <div className="backface-hidden absolute bottom-0 aspect-screen h-full translate-x-16 translate-y-16 -rotate-[15deg] skew-x-12 skew-y-3 overflow-hidden rounded-[20px] bg-white shadow-[0px_50px_100px_-25px_rgba(0,0,0,1)] transition-transform duration-1000 ease-in-out after:absolute after:inset-0 after:rounded-xl after:border after:border-white after:border-opacity-10 group-hover:translate-y-8 xs:h-[120%] xs:translate-x-32 xs:translate-y-32 xs:group-hover:translate-y-24">
          {homepageImageUrl && (
            <Image
              src={homepageImageUrl}
              alt={`${app.name} homepage screenshot`}
              fill
              sizes="(max-width: 480px) 360px, 500px"
              className="object-cover object-left-top"
              draggable={false}
            />
          )}
        </div>
      </div>

      <div className="relative flex w-full items-center gap-4">
        <AppIcon
          icon={app.icon}
          glyph={app.glyph}
          name={app.name}
          background={backgroundColor}
          size={48}
        />
        <div className="flex flex-col gap-0.5 pt-0.5">
          <span className="line-clamp-1 text-16 font-semibold leading-tight text-gray-950 dark:text-white lg:text-17">
            {app.name}
          </span>
          <span className="text-13 text-gray-600">{`${primaryCategory?.name}, ${dealsToDisplay.length} deal${dealsToDisplay.length === 1 ? '' : 's'}`}</span>
        </div>
      </div>
    </Link>
  )
}

export function CornerGradient() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="160"
      height="160"
      viewBox="0 0 160 160"
      fill="none"
    >
      <g filter="url(#filter0_f_26146_1323)">
        <path
          d="M0 -30C0 -46.5685 13.4315 -60 30 -60H70C86.5685 -60 100 -46.5685 100 -30C100 -13.4315 86.5685 0 70 0H30C13.4315 0 0 -13.4315 0 -30Z"
          fill="#6D28D9"
        />
      </g>
      <g filter="url(#filter1_f_26146_1323)">
        <path
          d="M-40 -10C-40 -37.6142 -17.6142 -60 10 -60C37.6142 -60 60 -37.6142 60 -10C60 17.6142 37.6142 40 10 40C-17.6142 40 -40 17.6142 -40 -10Z"
          fill="#C026D3"
        />
      </g>
      <g filter="url(#filter2_f_26146_1323)">
        <path
          d="M-80 -10C-80 -37.6142 -57.6142 -60 -30 -60C-2.38577 -60 20 -37.6142 20 -10V50C20 77.6142 -2.38577 100 -30 100C-57.6142 100 -80 77.6142 -80 50V-10Z"
          fill="#C80844"
        />
      </g>
      <defs>
        <filter
          id="filter0_f_26146_1323"
          x="-60"
          y="-120"
          width="220"
          height="180"
          filterUnits="userSpaceOnUse"
          colorInterpolationFilters="sRGB"
        >
          <feFlood floodOpacity="0" result="BackgroundImageFix" />
          <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape" />
          <feGaussianBlur stdDeviation="30" result="effect1_foregroundBlur_26146_1323" />
        </filter>
        <filter
          id="filter1_f_26146_1323"
          x="-100"
          y="-120"
          width="220"
          height="220"
          filterUnits="userSpaceOnUse"
          colorInterpolationFilters="sRGB"
        >
          <feFlood floodOpacity="0" result="BackgroundImageFix" />
          <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape" />
          <feGaussianBlur stdDeviation="30" result="effect1_foregroundBlur_26146_1323" />
        </filter>
        <filter
          id="filter2_f_26146_1323"
          x="-140"
          y="-120"
          width="220"
          height="280"
          filterUnits="userSpaceOnUse"
          colorInterpolationFilters="sRGB"
        >
          <feFlood floodOpacity="0" result="BackgroundImageFix" />
          <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape" />
          <feGaussianBlur stdDeviation="30" result="effect1_foregroundBlur_26146_1323" />
        </filter>
      </defs>
    </svg>
  )
}
